import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { DiscountType, DiscountDurationType } from '../../../../Data/SD';
import { useAuth } from '../../../../firebase/AuthProvider';
import { SubscriptionDto, UpsertOrganizationDiscountRequest } from '../../../../interfaces';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { UpsertOrganizationDiscountAsync } from '../../../../services/PaymentServices/SubscriptionService';
import { useAbortManager } from '../../../../hooks/cancelletionManagerHook/useAbortManager';

interface EditDiscountModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    subscription: SubscriptionDto;
    orgId: number
}

const EditDiscountModal: React.FC<EditDiscountModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    subscription,
    orgId
}) => {
    const cancelletionManager = useAbortManager();
    const { currentUser } = useAuth();
    const [discountForm, setDiscountForm] = useState({
        type: DiscountType.PERCENT,
        value: 0,
        durationType: DiscountDurationType.ONE_TIME,
        validFrom: null as string | null,
        validTo: null as string | null
    });
    const [errors, setErrors] = useState<{ type?: string; value?: string; durationType?: string }>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && subscription.orgDiscount) {
            setDiscountForm({
                type: subscription.orgDiscount.discountType as DiscountType,
                value: subscription.orgDiscount.discountValue,
                durationType: subscription.orgDiscount.durationType as DiscountDurationType,
                validFrom: subscription.orgDiscount.validFrom,
                validTo: subscription.orgDiscount.validTo
            });
            setErrors({});
            setLoading(false);
        }
    }, [isOpen, subscription.orgDiscount]);

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setDiscountForm(prev => ({
            ...prev,
            [name]:
                name === 'value' ? Number(value) :
                    name === 'durationInCycles' ? (value ? Number(value) : null) :
                        name === 'validFrom' || name === 'validTo' ? (value || null) :
                            value,
            ...(name === 'type' && value === DiscountType.NONE ? { value: 0 } : {})
        }));
        setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    const validateForm = () => {
        const newErrors: { type?: string; value?: string } = {};
        if (!discountForm.type) {
            newErrors.type = 'Please select a discount type.';
        }
        if (discountForm.type === DiscountType.PERCENT) {
            if (discountForm.value < 0 || discountForm.value > 100) {
                newErrors.value = 'Percent must be between 0 and 100.';
            }
        } else if (discountForm.type === DiscountType.FIXED) {
            if (discountForm.value <= 0) {
                newErrors.value = 'Fixed amount must be greater than 0.';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setLoading(true);
        await UpdateDiscountOfOrg();
    };

    const UpdateDiscountOfOrg = async () => {
        try {
            var token = await currentUser?.getIdToken();
            if (!token) return;

            var payload: UpsertOrganizationDiscountRequest = {
                organizationId: orgId,
                discountType: discountForm.type,
                discountValue: discountForm.type === DiscountType.NONE ? 0 : discountForm.value,
                durationType: discountForm.durationType.toString(),
                durationInCycles: null,
                validFrom: discountForm.validFrom,
                validTo: discountForm.validTo
            }

            var controller = cancelletionManager.create("UpdateDiscount");
            var res = await UpsertOrganizationDiscountAsync(token, payload, controller.signal);
            if (res.success) {
                onSuccess();
            }
        } catch (error: any) {
            if (error.name === 'AbortError') return;
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to update discount");
        } finally {
            setLoading(false);
            cancelletionManager.remove("UpdateDiscount");
        }
    }

    return (
        <Modal show={isOpen} onHide={onClose} centered className="custom_popup" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Edit Discount</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label htmlFor="type" className="form-label">Discount Type</label>
                        <select
                            id="type"
                            name="type"
                            value={discountForm.type}
                            onChange={handleFormChange}
                            className="form-select"
                            disabled={loading}
                        >
                            <option value={DiscountType.NONE}>NONE</option>
                            <option value={DiscountType.PERCENT}>PERCENT</option>
                            <option value={DiscountType.FIXED}>FIXED</option>
                        </select>
                        {errors.type && <div className="text-danger">{errors.type}</div>}
                    </div>
                    <div className="mb-3">
                        <label htmlFor="value" className="form-label">Discount Value (in $)</label>
                        <input
                            id="value"
                            name="value"
                            type="number"
                            value={discountForm.value}
                            onChange={handleFormChange}
                            className="form-control"
                            min={discountForm.type === DiscountType.PERCENT ? 0 : 0.01}
                            max={discountForm.type === DiscountType.PERCENT ? 100 : undefined}
                            step={discountForm.type === DiscountType.PERCENT ? 1 : 0.01}
                            disabled={loading || discountForm.type === DiscountType.NONE}
                        />
                        {errors.value && <div className="text-danger">{errors.value}</div>}
                    </div>
                    <div className="mb-3">
                        <label htmlFor="durationType" className="form-label">Duration Type <span className="text-danger">*</span></label>
                        <select
                            id="durationType"
                            name="durationType"
                            value={discountForm.durationType}
                            onChange={handleFormChange}
                            className="form-select"
                            disabled={loading}
                        >
                            <option value={DiscountDurationType.ONE_TIME}>One Time</option>
                            <option value={DiscountDurationType.RECURRING_UNTIL_CANCELLED}>RECURRING_UNTIL_CANCELLED</option>
                            <option value={DiscountDurationType.RECURRING_UNTIL_DATE}>RECURRING UNTIL DATE</option>
                        </select>
                        {errors.durationType && <div className="text-danger">{errors.durationType}</div>}
                    </div>

                    <div className="mb-3">
                        <label htmlFor="validFrom" className="form-label">Valid From <span className="text-muted">(Optional)</span></label>
                        <input
                            id="validFrom"
                            name="validFrom"
                            type="date"
                            value={discountForm.validFrom || ''}
                            onChange={handleFormChange}
                            className="form-control"
                            disabled={loading}
                        />
                    </div>
                    {discountForm.durationType === DiscountDurationType.RECURRING_UNTIL_DATE && (
                        <div className="mb-3">
                            <label htmlFor="validTo" className="form-label">Valid To <span className="text-muted">(Optional)</span></label>
                            <input
                                id="validTo"
                                name="validTo"
                                type="date"
                                value={discountForm.validTo || ''}
                                onChange={handleFormChange}
                                className="form-control"
                                disabled={loading}
                            />
                        </div>
                    )}
                    <div className="d-flex justify-content-end">
                        <Button variant="secondary" onClick={onClose} className="me-2" disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? (
                                <>
                                    <Spinner size="sm" /> Updating Discount
                                </>
                            ) : (
                                'Update Discount'
                            )}
                        </Button>
                    </div>
                    <br />
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default EditDiscountModal;